import { UserRole } from "@eisox/api/core-api";
import { IconButton, Page, Sidebar } from "@eisox/design-system";
import { PanelLeftIcon } from "@eisox/icons";
import { useMediaQuery } from "@eisox/tools";
import { createFileRoute, Outlet, useLocation, useRouter } from "@tanstack/react-router";

import { ActionDropdown, DisplayDropdown } from "~/features/maintenance";
import { buildingPageHead } from "~/helpers/document-title.helper";
import { useUserPreferenceStore } from "~/integrations/zustand/stores/user-preference.store";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/maintenance")({
  head: ({ match: { context }, params: { buildingId } }) =>
    buildingPageHead(context.queryClient, buildingId, m.maintenance()),
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const router = useRouter();
  const { pathname } = useLocation();
  const { buildingId } = Route.useParams();

  const isMobile = useMediaQuery("(max-width: 768px)");
  const { building, date, gateways, role } = useBuildingContext(Route.id);

  const setMaintenanceAdvanced = useUserPreferenceStore(s => s.setMaintenanceAdvanced);
  const advanced = pathname.includes("/maintenance/advanced");

  const handleAdvancedCheckedChange = (checked: boolean | null) => {
    setMaintenanceAdvanced(checked ?? false);
    void navigate({
      to: checked ? "/buildings/$buildingId/maintenance/advanced" : "/buildings/$buildingId/maintenance",
      params: { buildingId },
    });
  };

  if (!advanced) {
    return <Outlet />;
  }

  return (
    <Page.Root>
      <Page.Header>
        {isMobile && (
          <Sidebar.Trigger asChild>
            <IconButton>
              <PanelLeftIcon />
            </IconButton>
          </Sidebar.Trigger>
        )}
        <Page.Title>{isMobile ? building.name : m.maintenance()}</Page.Title>
        <div className="gap-2.5 md:gap-5 ml-auto flex items-center">
          <DisplayDropdown advancedChecked={advanced} onAdvancedCheckedChange={handleAdvancedCheckedChange} />
          {role === UserRole.INSTALLER && (
            <ActionDropdown
              routeId={Route.id}
              buildingId={buildingId}
              updatedAt={date}
              onRefresh={() => {
                void router.invalidate();
              }}
              onClickOnFloor={floorId => {
                void navigate({
                  to: "/buildings/$buildingId/floors/$floorId",
                  params: { buildingId, floorId },
                });
              }}
              gateways={gateways}
            />
          )}
        </div>
      </Page.Header>
      <Outlet />
    </Page.Root>
  );
}
